import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { Risk } from '../../types/api';
import {
  extractRiskCityName,
  getWorldCitiesIsoIndexes,
  loadWorldCityRecords,
  type RiskLocationMatch,
} from '../../functions/geospatial';
import { geocodeResolve } from '../../functions/apis/geocodeService';
import {
  riskInCountry,
  countOppRiskPerGeoName,
  totalOppRisk,
  type GeoOppRiskCounts,
} from './coverageMapCountryMatch';
import type { UnitLocationRow } from '../../functions/apis/unitCoverageService';
import {
  aggregateUnitCountByGeoName,
  fillColorForUnitCount,
  type UnitCoverageGeoIsoContext,
} from './unitCoverageByCountry';

/**
 * Bundled copy of johan/world.geo.json `countries.geo.json` under `public/data/`.
 * Avoids `raw.githubusercontent.com`, which is often blocked on corporate networks
 * (tiles from other domains can still work, so other maps look fine).
 */
const GEOJSON_URL = `${import.meta.env.BASE_URL}data/countries.geo.json`;

/** GeoJSON ``Feature.id`` in this bundle is ISO 3166-1 alpha-3 (e.g. ``USA``). */
function iso3ToGeoNameFromFeatures(data: GeoJSON.FeatureCollection): Map<string, string> {
  const map = new Map<string, string>();
  for (const f of data.features) {
    const name = (f.properties as { name?: string })?.name;
    const idRaw = (f as GeoJSON.Feature & { id?: string | number }).id;
    const id = typeof idRaw === 'string' ? idRaw.trim().toUpperCase() : '';
    if (name && /^[A-Z]{3}$/.test(id)) {
      map.set(id, name);
    }
  }
  return map;
}

function unitCoverageIsoContext(data: GeoJSON.FeatureCollection): UnitCoverageGeoIsoContext {
  return {
    iso3ToGeoName: iso3ToGeoNameFromFeatures(data),
    worldCitiesIso: getWorldCitiesIsoIndexes(),
  };
}

/** Neutral — no mapped data (table: empty / gray) */
const COLOR_NEUTRAL = 'rgba(148, 163, 184, 0.12)';
/**
 * Opportunity LVL 1 (light) → LVL 3 (dark). Emerald/teal palette.
 * LVL1 #6ee7b7 · LVL2 #2dd4bf · LVL3 #0d9488
 */
const OPP_LEVEL = ['#6ee7b7', '#2dd4bf', '#0d9488'] as const;
/**
 * Risk LVL 1 (light) → LVL 3 (dark). Refined rose/red palette.
 * LVL1 #fda4af · LVL2 #f43f5e · LVL3 #be123c
 */
const RISK_LEVEL = ['#fda4af', '#f43f5e', '#be123c'] as const;
function levelIndex(count: number, maxSameType: number): number {
  const maxN = Math.max(1, maxSameType);
  const t = Math.min(1, count / maxN);
  if (t <= 1 / 3) return 0;
  if (t <= 2 / 3) return 1;
  return 2;
}

function maxOppMaxRiskFromCounts(c: Map<string, GeoOppRiskCounts>): { maxOpp: number; maxRisk: number } {
  let maxOpp = 0;
  let maxRisk = 0;
  c.forEach((v) => {
    maxOpp = Math.max(maxOpp, v.opportunities);
    maxRisk = Math.max(maxRisk, v.risks);
  });
  return { maxOpp: Math.max(1, maxOpp), maxRisk: Math.max(1, maxRisk) };
}

function strokeForOppRisk(opp: number, risk: number): string {
  const total = opp + risk;
  if (total <= 0) return '#475569';
  return risk > opp ? RISK_LEVEL[2] : OPP_LEVEL[2];
}

/** Crisp country outlines in detail (covered-area) view. */
const BORDER_LINE_OPTS = { lineJoin: 'round' as const, lineCap: 'round' as const };

/** Zoom at/above this (or an explicit country selection) shows opportunity vs risk border colours. */
const DETAIL_BORDER_MIN_ZOOM = 4;

function showRiskOppRiskBorders(selected: string | null, mapZoom: number): boolean {
  return selected !== null || mapZoom >= DETAIL_BORDER_MIN_ZOOM;
}

const COUNTRY_PULSE_CLASS = 'coverage-country--pulse';

function shouldPulseCountryOutline(
  selected: string | null,
  mapZoom: number,
  row: GeoOppRiskCounts,
  unitCount = 0
): boolean {
  /* World/overview: no explicit country selection yet — pulse if risks/opps or units map here. */
  if (selected !== null || mapZoom >= DETAIL_BORDER_MIN_ZOOM) return false;
  return totalOppRisk(row) > 0 || unitCount > 0;
}

function setCountryPulseClass(path: L.Path, on: boolean): void {
  const el = path.getElement?.() as SVGElement | null | undefined;
  if (!el) return;
  el.classList.toggle(COUNTRY_PULSE_CLASS, on);
}

const LAND_FILL_NEUTRAL = '#eef2f1';
const BORDER_NEUTRAL_OVERVIEW = '#8b9aab';
const BORDER_WEIGHT_OVERVIEW = 1;
const BORDER_COVERED_OVERVIEW = '#4f46e5';
const BORDER_WEIGHT_COVERED_OVERVIEW = 1.15;

/** Country choropleth: mapped risks plus unit counts (no per-unit lat/lng markers). */
function baseCountryStyle(
  row: GeoOppRiskCounts,
  maxOpp: number,
  maxRisk: number,
  unitCount: number,
  showRiskOppBorders: boolean
): L.PathOptions {
  const n = totalOppRisk(row);
  const hasSignal = unitCount > 0 || n > 0;
  const hasCoverage = unitCount > 0;
  if (!showRiskOppBorders) {
    return {
      fillColor: fillColorForUnitCount(unitCount, LAND_FILL_NEUTRAL),
      fillOpacity: hasCoverage ? 0.95 : 1,
      color: hasCoverage ? BORDER_COVERED_OVERVIEW : BORDER_NEUTRAL_OVERVIEW,
      weight: hasCoverage ? BORDER_WEIGHT_COVERED_OVERVIEW : BORDER_WEIGHT_OVERVIEW,
      ...BORDER_LINE_OPTS,
    };
  }
  return {
    fillColor: fillColorForUnitCount(unitCount, COLOR_NEUTRAL),
    fillOpacity: hasSignal ? 0.82 : 0.35,
    color: strokeForOppRisk(row.opportunities, row.risks),
    /* Resting-state outline in detail view (hover uses a slimmer line below). */
    weight: hasSignal ? 3.25 : 2.25,
    ...BORDER_LINE_OPTS,
  };
}

/** Lighter fill for the selected country so pins are clearly visible. */
function selectedCountryStyle(
  row: GeoOppRiskCounts,
  maxOpp: number,
  maxRisk: number,
  unitCount: number
): L.PathOptions {
  return {
    fillColor: fillColorForUnitCount(unitCount, COLOR_NEUTRAL),
    fillOpacity: 0.42,
    color: strokeForOppRisk(row.opportunities, row.risks),
    weight: 4.25,
    ...BORDER_LINE_OPTS,
  };
}

/** When a country is selected: fade others, emphasize selected outline. */
function overlayStyle(
  name: string,
  row: GeoOppRiskCounts,
  maxOpp: number,
  maxRisk: number,
  selected: string | null,
  unitCount: number,
  showRiskOppBorders: boolean
): L.PathOptions {
  if (!selected) return baseCountryStyle(row, maxOpp, maxRisk, unitCount, showRiskOppBorders);
  if (name === selected) return selectedCountryStyle(row, maxOpp, maxRisk, unitCount);
  return {
    fillColor: '#64748b',
    fillOpacity: 0.05,
    color: 'rgba(71, 85, 105, 0.5)',
    weight: 1.1,
    opacity: 0.9,
    ...BORDER_LINE_OPTS,
  };
}

function hoverStyleRow(
  row: GeoOppRiskCounts,
  maxOpp: number,
  maxRisk: number,
  unitCount: number
): L.PathOptions {
  const r = row.risks;
  const o = row.opportunities;
  const riskDominant = r > o;
  const n = totalOppRisk(row);
  const hasSignal = unitCount > 0 || n > 0;
  /* Slightly slimmer than resting detail outline so hover does not read as “heavier”. */
  const hoverWeight = hasSignal ? 2.5 : 1.85;
  return {
    fillColor: fillColorForUnitCount(unitCount, COLOR_NEUTRAL),
    fillOpacity: 0.95,
    color: riskDominant ? RISK_LEVEL[2] : OPP_LEVEL[2],
    weight: hoverWeight,
    ...BORDER_LINE_OPTS,
  };
}

function hoverStyleWithSelection(
  name: string,
  row: GeoOppRiskCounts,
  maxOpp: number,
  maxRisk: number,
  selected: string | null,
  unitCount: number
): L.PathOptions {
  if (!selected) return hoverStyleRow(row, maxOpp, maxRisk, unitCount);
  if (name === selected) {
    /* Same outline weight as resting selection — highlight is fill/opacity, not a thicker ring. */
    return selectedCountryStyle(row, maxOpp, maxRisk, unitCount);
  }
  return {
    fillColor: '#94a3b8',
    fillOpacity: 0.12,
    color: 'rgba(71, 85, 105, 0.55)',
    weight: 0.95,
    ...BORDER_LINE_OPTS,
  };
}

function escapeHtml(text: string): string {
  const d = document.createElement('div');
  d.textContent = text;
  return d.innerHTML;
}

function coverageTooltipHtml(name: string, opp: number, risk: number): string {
  const total = opp + risk;
  const countPart = total
    ? `<div class="coverage-tip-stats"><span class="coverage-tip-badge coverage-tip-badge--opp">${opp}</span><span class="coverage-tip-badge coverage-tip-badge--risk">${risk}</span></div><span class="coverage-tip-legend">opportunities · risks (border)</span>`
    : '';
  return `<div class="coverage-tip"><span class="coverage-tip-label">Coverage</span><span class="coverage-tip-name">${escapeHtml(name)}</span>${countPart}</div>`;
}

/** Ray-cast: point [lng,lat] inside ring (GeoJSON ring, closed or not). */
function pointInRing(lng: number, lat: number, ring: GeoJSON.Position[]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const cross = yi > lat !== yj > lat;
    if (cross && lng < ((xj - xi) * (lat - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
  }
  return inside;
}

function pointInPolygonCoords(lng: number, lat: number, rings: GeoJSON.Position[][]): boolean {
  if (!rings[0]?.length) return false;
  if (!pointInRing(lng, lat, rings[0])) return false;
  for (let h = 1; h < rings.length; h++) {
    if (pointInRing(lng, lat, rings[h])) return false;
  }
  return true;
}

function pointInGeometry(lng: number, lat: number, g: GeoJSON.Geometry): boolean {
  if (g.type === 'Polygon') {
    return pointInPolygonCoords(lng, lat, g.coordinates as GeoJSON.Position[][]);
  }
  if (g.type === 'MultiPolygon') {
    const polys = g.coordinates as GeoJSON.Position[][][];
    for (const poly of polys) {
      if (pointInPolygonCoords(lng, lat, poly)) return true;
    }
  }
  return false;
}

/** Pin icons from public folder. */
const PIN_IMG_RISK = '/images/RISK.png';
const PIN_IMG_OPP = '/images/OPP.png';
const PIN_IMG_MIXED = '/images/OPP-RISK.png';

type PinKind = 'risk' | 'opportunity' | 'mixed';

function getPinOuterFromMarker(marker: L.Marker): HTMLElement | null {
  const el = marker.getElement();
  if (!el) return null;
  return el.querySelector('.coverage-pin-outer') as HTMLElement | null;
}

function setPinPulseClass(marker: L.Marker, className: string, on: boolean): void {
  const outer = getPinOuterFromMarker(marker);
  if (!outer) return;
  outer.classList.toggle(className, on);
}

function createPin(kind: PinKind) {
  const src =
    kind === 'risk' ? PIN_IMG_RISK : kind === 'opportunity' ? PIN_IMG_OPP : PIN_IMG_MIXED;
  const alt = kind === 'risk' ? 'Risk' : kind === 'opportunity' ? 'Opportunity' : 'Risk & Opportunity';
  return L.divIcon({
    className: 'coverage-pin-marker',
    html: `<div class="coverage-pin-outer" data-pin-kind="${kind}"><span class="coverage-pin-pulse" aria-hidden="true"></span><img class="coverage-pin-img" src="${src}" alt="${alt}" /></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

function geoNameForSidebarCountry(
  sidebarCountry: string,
  features: GeoJSON.Feature[]
): string | null {
  const trim = (sidebarCountry || '').trim();
  if (!trim) return null;
  for (const f of features) {
    const name = (f.properties as { name?: string })?.name;
    if (!name) continue;
    if (riskInCountry(trim, trim, name)) return name;
  }
  return null;
}

export interface CoverageWorldMapProps {
  matches: RiskLocationMatch[];
  loading?: boolean;
  /** Sidebar country → fit map to that country (GeoJSON bounds). */
  filterCountry?: string;
  /** Sidebar city (normalized) → fit to markers or geocode. */
  filterCity?: string;
  /** Called when user selects a country (or clears): country name and risks in that country. */
  onSelectionChange?: (country: string | null, risks: Risk[]) => void;
  /** Unit buckets from ``GET /coverage/unit_locations/`` — used for country choropleth only (no lat/lng markers). */
  unitLocations?: UnitLocationRow[];
}

export const CoverageWorldMap = ({
  matches,
  loading = false,
  filterCountry = '',
  filterCity = '',
  onSelectionChange,
  unitLocations = [],
}: CoverageWorldMapProps) => {
  const unitLocationsRef = useRef(unitLocations);
  unitLocationsRef.current = unitLocations;

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const geoLayerRef = useRef<L.GeoJSON | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const geoDataRef = useRef<GeoJSON.FeatureCollection | null>(null);
  const countsRef = useRef<Map<string, GeoOppRiskCounts>>(new Map());
  /** Per GeoJSON ``properties.name``: aggregated unit counts (ISO-aware when world cities loaded). */
  const unitsByGeoRef = useRef<Map<string, number>>(new Map());
  const maxRef = useRef(0);
  const hoverLayerRef = useRef<L.Path | null>(null);
  const selectedCountryRef = useRef<string | null>(null);
  const onSelectionChangeRef = useRef(onSelectionChange);
  /** When true, we are flying after a click or filter — do not clear selection on zoom < DETAIL_BORDER_MIN_ZOOM. */
  const flyingProgrammaticallyRef = useRef(false);
  onSelectionChangeRef.current = onSelectionChange;
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  /** Bumps when GeoJSON layer is attached so filter zoom can run. */
  const [geoReadyTick, setGeoReadyTick] = useState(0);
  /** Drives border restyle when zoom crosses the detail threshold. */
  const [mapZoomLevel, setMapZoomLevel] = useState(2.35);
  selectedCountryRef.current = selectedCountry;

  const onCountryClick = useCallback((name: string) => {
    setSelectedCountry(name);
  }, []);

  const clearCountry = useCallback(() => {
    setSelectedCountry(null);
    mapRef.current?.flyTo([22, 10], 2.4, { duration: 0.6 });
  }, []);

  const prevFiltersRef = useRef({ country: '', city: '' });
  const geocodeOnceRef = useRef<string>('');

  /** Zoom/pan from sidebar country + city filters */
  useEffect(() => {
    const map = mapRef.current;
    const geoLayer = geoLayerRef.current;
    const data = geoDataRef.current;
    if (!map) return;

    const c = (filterCountry || '').trim();
    const city = (filterCity || '').trim();
    const prev = prevFiltersRef.current;
    const filtersCleared = !c && !city && (prev.country || prev.city);
    prevFiltersRef.current = { country: c, city };

    if (!c && !city) {
      if (filtersCleared) {
        setSelectedCountry(null);
        map.flyTo([22, 10], 2.4, { duration: 0.55 });
      }
      return;
    }

    const flyOpts = { duration: 0.65, maxZoom: 10 } as const;

    /* City: prefer bounds of matching markers */
    if (city && matches.length > 0) {
      let pts = matches.filter((m) => extractRiskCityName(m.risk.location) === city);
      if (c) {
        const geoName = data?.features?.length ? geoNameForSidebarCountry(c, data.features) : null;
        pts = pts.filter((m) =>
          geoName
            ? riskInCountry(m.risk.country, m.matchedCountry, geoName)
            : (m.risk.country || '').trim() === c || (m.matchedCountry || '').includes(c)
        );
      }
      if (pts.length >= 1) {
        const bounds = L.latLngBounds(pts.map((m) => [m.lat, m.lng] as L.LatLngTuple));
        if (bounds.isValid()) {
          if (c && data?.features?.length) {
            const geoName = geoNameForSidebarCountry(c, data.features);
            if (geoName) setSelectedCountry(geoName);
          }
          flyingProgrammaticallyRef.current = true;
          map.flyToBounds(bounds.pad(0.35), { ...flyOpts, maxZoom: pts.length === 1 ? 11 : 10 });
          setTimeout(() => {
            flyingProgrammaticallyRef.current = false;
          }, 1500);
          geocodeOnceRef.current = '';
          return;
        }
      }
    }

    /* Country only: GeoJSON bounds */
    if (c && !city && geoLayer && data?.features?.length) {
      const geoName = geoNameForSidebarCountry(c, data.features);
      if (geoName) {
        setSelectedCountry(geoName);
        let done = false;
        geoLayer.eachLayer((ly) => {
          if (done) return;
          const feat = (ly as L.Layer & { feature?: GeoJSON.Feature }).feature;
          const name = (feat?.properties as { name?: string })?.name || '';
          if (name !== geoName) return;
          try {
            const b = (ly as L.Polygon).getBounds?.();
            if (b?.isValid()) {
              flyingProgrammaticallyRef.current = true;
              map.flyToBounds(b.pad(0.12), { duration: 0.6, maxZoom: 6 });
              setTimeout(() => {
                flyingProgrammaticallyRef.current = false;
              }, 1500);
              done = true;
            }
          } catch {
            /* ignore */
          }
        });
        if (done) {
          geocodeOnceRef.current = '';
          return;
        }
      }
    }

    /* City but no markers yet (or no match): geocode once per filter combo */
    if (city && !loading) {
      const geoKey = `${c}|${city}`;
      if (geocodeOnceRef.current !== geoKey) {
        geocodeOnceRef.current = geoKey;
        let cancelled = false;
        geocodeResolve(city, c)
          .then((res) => {
            if (cancelled || !mapRef.current) return;
            if (c && geoDataRef.current?.features?.length) {
              const geoName = geoNameForSidebarCountry(c, geoDataRef.current.features);
              if (geoName) setSelectedCountry(geoName);
            }
            flyingProgrammaticallyRef.current = true;
            mapRef.current.flyTo([res.lat, res.lng], c ? 9 : 8, { duration: 0.6 });
            setTimeout(() => {
              flyingProgrammaticallyRef.current = false;
            }, 1500);
          })
          .catch(() => {
            geocodeOnceRef.current = '';
          });
        return () => {
          cancelled = true;
        };
      }
    }

    return undefined;
  }, [filterCountry, filterCity, matches, loading, geoReadyTick]);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [24, 15],
      zoom: 2.35,
      minZoom: 2,
      maxZoom: 12,
      maxBounds: [
        [-70, -190],
        [85, 210],
      ],
      scrollWheelZoom: true,
      zoomControl: true,
      attributionControl: true,
    });

    /*
     * Country shapes are the bundled GeoJSON. Carto basemap tiles now require an
     * API key and paint "API KEY REQUIRED" instead of coastlines and borders.
     */

    mapRef.current = map;
    markersLayerRef.current = L.layerGroup().addTo(map);

    const syncMapZoom = () => setMapZoomLevel(map.getZoom());
    map.on('zoomend', syncMapZoom);
    syncMapZoom();

    const invalidate = () => map.invalidateSize();
    requestAnimationFrame(invalidate);
    [200, 500, 1000].forEach((ms) => setTimeout(invalidate, ms));
    window.addEventListener('resize', invalidate);
    const ro = new ResizeObserver(invalidate);
    ro.observe(containerRef.current);

    fetch(GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error('geo');
        return r.json();
      })
      .then(async (data: GeoJSON.FeatureCollection) => {
        geoDataRef.current = data;
        try {
          await loadWorldCityRecords();
        } catch {
          /* unit choropleth may fall back to fuzzy ``riskInCountry`` */
        }
        const names = data.features
          .map((f) => (f.properties as { name?: string })?.name)
          .filter(Boolean) as string[];
        const c = countOppRiskPerGeoName(matches, names);
        const { maxOpp, maxRisk } = maxOppMaxRiskFromCounts(c);
        countsRef.current = c;
        maxRef.current = maxOpp + maxRisk;
        const isoCtx = unitCoverageIsoContext(data);
        const unitMap = aggregateUnitCountByGeoName(unitLocationsRef.current, names, isoCtx);
        unitsByGeoRef.current = unitMap;

        let viewDebounce: ReturnType<typeof setTimeout> | null = null;

        const layer = L.geoJSON(data, {
          style: (feat) => {
            const name = (feat?.properties as { name?: string })?.name || '';
            const row = c.get(name) || { opportunities: 0, risks: 0 };
            const uc = unitsByGeoRef.current.get(name) ?? 0;
            const showStroke = showRiskOppRiskBorders(selectedCountryRef.current, map.getZoom());
            return baseCountryStyle(row, maxOpp, maxRisk, uc, showStroke);
          },
          onEachFeature: (feat, lyr) => {
            const name = (feat.properties as { name?: string })?.name || 'Country';
            const row = c.get(name) || { opportunities: 0, risks: 0 };
            const path = lyr as L.Path;
            path.bindTooltip(coverageTooltipHtml(name, row.opportunities, row.risks), {
              sticky: true,
              direction: 'auto',
              className: 'coverage-tip-box',
              opacity: 1,
            });
            path.on('mouseover', () => {
              const sel = selectedCountryRef.current;
              const z = map.getZoom();
              const showStroke = showRiskOppRiskBorders(sel, z);
              const { maxOpp: mo, maxRisk: mr } = maxOppMaxRiskFromCounts(countsRef.current);
              const rowCur = countsRef.current.get(name) || { opportunities: 0, risks: 0 };
              const uCur = unitsByGeoRef.current.get(name) ?? 0;
              if (hoverLayerRef.current && hoverLayerRef.current !== path) {
                const f = (hoverLayerRef.current as L.Layer & { feature?: GeoJSON.Feature }).feature;
                const nm = (f?.properties as { name?: string })?.name || '';
                const rowPrev = countsRef.current.get(nm) || { opportunities: 0, risks: 0 };
                const uPrev = unitsByGeoRef.current.get(nm) ?? 0;
                setCountryPulseClass(hoverLayerRef.current as L.Path, false);
                hoverLayerRef.current.setStyle(overlayStyle(nm, rowPrev, mo, mr, sel, uPrev, showStroke));
              }
              path.setStyle(hoverStyleWithSelection(name, rowCur, mo, mr, sel, uCur));
              setCountryPulseClass(path, shouldPulseCountryOutline(sel, z, rowCur, uCur));
              hoverLayerRef.current = path;
            });
            path.on('mouseout', () => {
              const rowMo = countsRef.current.get(name) || { opportunities: 0, risks: 0 };
              const { maxOpp: mo, maxRisk: mr } = maxOppMaxRiskFromCounts(countsRef.current);
              const uMo = unitsByGeoRef.current.get(name) ?? 0;
              const sel = selectedCountryRef.current;
              const showStroke = showRiskOppRiskBorders(sel, map.getZoom());
              setCountryPulseClass(path, false);
              path.setStyle(overlayStyle(name, rowMo, mo, mr, sel, uMo, showStroke));
            });
            path.on('click', () => {
              /* Cancel any pending view-based selection so it cannot overwrite this click. */
              if (viewDebounce) {
                clearTimeout(viewDebounce);
                viewDebounce = null;
              }
              onCountryClick(name);
              flyingProgrammaticallyRef.current = true;
              try {
                const b = (path as L.Polygon).getBounds?.();
                if (b?.isValid()) {
                  map.flyToBounds(b.pad(0.12), { duration: 0.55, maxZoom: 6 });
                  setTimeout(() => {
                    flyingProgrammaticallyRef.current = false;
                  }, 1500);
                } else {
                  flyingProgrammaticallyRef.current = false;
                }
              } catch {
                flyingProgrammaticallyRef.current = false;
              }
            });
          },
        });
        layer.addTo(map);
        geoLayerRef.current = layer;
        /* Risk pins above choropleth (re-append raises z-order). */
        const markersGroup = markersLayerRef.current;
        if (markersGroup) {
          markersGroup.remove();
          markersGroup.addTo(map);
        }
        setGeoReadyTick((t) => t + 1);

        /* Zoom/pan into a country that has risks → same as selecting it (pins + toolbar). */
        const applySelectionFromView = () => {
          viewDebounce = null;
          /* Ignore view-based selection entirely while we are flying after a click or filter. */
          if (flyingProgrammaticallyRef.current) return;
          if (map.getZoom() < DETAIL_BORDER_MIN_ZOOM) {
            setSelectedCountry(null);
            return;
          }
          const { lng, lat } = map.getCenter();
          const coll = geoDataRef.current;
          const counts = countsRef.current;
          if (!coll?.features?.length) return;
          let anyAtCenter: string | null = null;
          for (const f of coll.features) {
            const name = (f.properties as { name?: string })?.name;
            if (!name || !f.geometry || !pointInGeometry(lng, lat, f.geometry)) continue;
            const rowC = counts.get(name) || { opportunities: 0, risks: 0 };
            const n = totalOppRisk(rowC);
            const u = unitsByGeoRef.current.get(name) || 0;
            if (n > 0 || u > 0) {
              setSelectedCountry(name);
              return;
            }
            if (!anyAtCenter) anyAtCenter = name;
          }
          if (anyAtCenter) setSelectedCountry(anyAtCenter);
          else setSelectedCountry(null);
        };
        const scheduleSelectionFromView = () => {
          if (viewDebounce) clearTimeout(viewDebounce);
          viewDebounce = setTimeout(applySelectionFromView, 100);
        };
        map.on('moveend', scheduleSelectionFromView);
        map.on('zoomend', scheduleSelectionFromView);
        const cleanupView = () => {
          if (viewDebounce) clearTimeout(viewDebounce);
          map.off('moveend', scheduleSelectionFromView);
          map.off('zoomend', scheduleSelectionFromView);
        };
        (map as L.Map & { __coverageViewCleanup?: () => void }).__coverageViewCleanup = cleanupView;
      })
      .catch((err: unknown) => {
        if (import.meta.env.DEV) console.warn('[CoverageWorldMap] GeoJSON failed', err);
        setGeoError('Map data unavailable.');
      });

    return () => {
      const m = mapRef.current as (L.Map & { __coverageViewCleanup?: () => void }) | null;
      m?.__coverageViewCleanup?.();
      window.removeEventListener('resize', invalidate);
      ro.disconnect();
      map.off('zoomend', syncMapZoom);
      map.remove();
      mapRef.current = null;
      geoLayerRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const layer = geoLayerRef.current;
      const data = geoDataRef.current;
      if (!layer || !data?.features) return;
      try {
        await loadWorldCityRecords();
      } catch {
        /* fuzzy fallback for units */
      }
      if (cancelled) return;
      const names = data.features
        .map((f) => (f.properties as { name?: string })?.name)
        .filter(Boolean) as string[];
      const c = countOppRiskPerGeoName(matches, names);
      const { maxOpp, maxRisk } = maxOppMaxRiskFromCounts(c);
      countsRef.current = c;
      maxRef.current = maxOpp + maxRisk;
      const isoCtx = unitCoverageIsoContext(data as GeoJSON.FeatureCollection);
      const unitMap = aggregateUnitCountByGeoName(unitLocations, names, isoCtx);
      unitsByGeoRef.current = unitMap;
      const sel = selectedCountry;
      const map = mapRef.current;
      const z = map?.getZoom() ?? mapZoomLevel;
      const showStroke = showRiskOppRiskBorders(sel, z);
      layer.eachLayer((ly) => {
        const feat = (ly as L.Layer & { feature?: GeoJSON.Feature }).feature;
        const name = (feat?.properties as { name?: string })?.name || '';
        const row = c.get(name) || { opportunities: 0, risks: 0 };
        const path = ly as L.Path;
        const uc = unitMap.get(name) ?? 0;
        path.setStyle(overlayStyle(name, row, maxOpp, maxRisk, sel, uc, showStroke));
        if (sel && name === sel) path.bringToFront();
        const html = coverageTooltipHtml(name, row.opportunities, row.risks);
        const tip = path.getTooltip();
        if (tip) tip.setContent(html);
        else
          path.bindTooltip(html, {
            sticky: true,
            direction: 'auto',
            className: 'coverage-tip-box',
            opacity: 1,
          });
      });
      hoverLayerRef.current = null;
    })();
    return () => {
      cancelled = true;
    };
  }, [matches, selectedCountry, unitLocations, mapZoomLevel]);

  useEffect(() => {
    const map = mapRef.current;
    const group = markersLayerRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const notify = onSelectionChangeRef.current;
    if (!selectedCountry) {
      notify?.(null, []);
      return;
    }
    const filtered = matches.filter((m) =>
      riskInCountry(m.risk.country, m.matchedCountry, selectedCountry)
    );
    const risksInCountry = filtered.map((m) => m.risk);
    notify?.(selectedCountry, risksInCountry);
    const byLatLng = new Map<string, RiskLocationMatch[]>();
    for (const m of filtered) {
      const k = `${m.lat.toFixed(3)},${m.lng.toFixed(3)}`;
      if (!byLatLng.has(k)) byLatLng.set(k, []);
      byLatLng.get(k)!.push(m);
    }
    byLatLng.forEach((grp) => {
      const first = grp[0];
      let oppN = 0;
      let riskN = 0;
      for (const m of grp) {
        if (m.risk.is_positive === true) oppN += 1;
        else if (m.risk.is_positive === false) riskN += 1;
      }
      const isMixed = riskN > 0 && oppN > 0;
      const isRisk = !isMixed && riskN > oppN;
      const pinKind: PinKind = isMixed ? 'mixed' : isRisk ? 'risk' : 'opportunity';
      const marker = L.marker([first.lat, first.lng], {
        icon: createPin(pinKind),
      });
      const kind = isMixed
        ? 'Risk & Opportunity'
        : first.risk.is_positive === true
          ? 'Opportunity'
          : first.risk.is_positive === false
            ? 'Risk'
            : 'Item';
      const kindClass = isMixed ? 'mixed' : kind.toLowerCase();
      /* Always show how many risks and opportunities at this location. */
      const riskPart = riskN > 0 ? `${riskN} risk${riskN !== 1 ? 's' : ''}` : '';
      const oppPart = oppN > 0 ? `${oppN} opportunit${oppN !== 1 ? 'ies' : 'y'}` : '';
      const countLine = [riskPart, oppPart].filter(Boolean).join(' · ') || `${grp.length} event${grp.length !== 1 ? 's' : ''}`;
      const detail =
        grp.length === 1
          ? first.risk.description?.slice(0, 160) || (first.risk.publication_date ? `Published ${String(first.risk.publication_date)}` : '')
          : '';
      const popupBody =
        countLine || detail
          ? `<div class="coverage-popup-content">${countLine ? `<div class="coverage-popup-counts">${escapeHtml(countLine)}</div>` : ''}${detail ? `<div class="coverage-popup-body">${escapeHtml(detail)}</div>` : ''}</div>`
          : '';
      marker.bindPopup(
        `<div class="coverage-popup"><div class="coverage-popup-header"><span class="coverage-popup-city">${escapeHtml(first.matchedCity)}</span><span class="coverage-popup-kind coverage-popup-kind--${kindClass}">${escapeHtml(kind)}</span></div>${popupBody}</div>`,
        { maxWidth: 240, className: 'coverage-popup-wrap' }
      );
      marker.on('mouseover', () => setPinPulseClass(marker, 'is-hovered', true));
      marker.on('mouseout', () => setPinPulseClass(marker, 'is-hovered', false));
      if (selectedCountry && riskInCountry(first.risk.country, first.matchedCountry, selectedCountry)) {
        setPinPulseClass(marker, 'is-selected', true);
      }
      marker.addTo(group);
    });
  }, [selectedCountry, matches]);

  return (
    <div className="coverage-world-map-wrap">
      <div className="coverage-world-map-chrome">
        <div className="coverage-world-map-toolbar">
          {selectedCountry ? (
            <>
              <button type="button" className="coverage-world-map-back" onClick={clearCountry}>
                ← World view
              </button>
              <span className="coverage-world-map-country">{selectedCountry}</span>
            </>
          ) : (
            <span className="coverage-world-map-title">Coverage</span>
          )}
        </div>
      </div>
      {geoError && <div className="coverage-world-map-error">{geoError}</div>}
      <div ref={containerRef} className="coverage-world-map" />
      {loading && (
        <div className="coverage-world-map-loading" aria-live="polite">
          <span className="coverage-world-map-loading-pulse" />
        </div>
      )}
    </div>
  );
};
